import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Store, User } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("type") === "dealer" ? "dealer" : "private";
  const [form, setForm] = useState({
    role: initialRole,
    salutation: "",
    first_name: "",
    last_name: "",
    street: "",
    house_number: "",
    zip_code: "",
    city: "",
    country: "Deutschland",
    name: "",
    company: "",
    tax_number: "",
    email: "",
    phone: "",
    address: "",
    password: "",
    password_confirm: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isDealer = form.role === "dealer";

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    setLoading(true); setError("");
    try {
      const { password_confirm, ...payload } = form;
      const user = await register(payload);
      if (user.role === "dealer") {
        toast.success("Händlerkonto erstellt — wartet auf Admin-Freigabe.");
        nav("/dashboard", { replace: true });
      } else {
        toast.success("Privatkonto erstellt.");
        nav("/fahrzeuge", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registrierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrap py-20 max-w-2xl">
      <div className="swiss-label text-[#16A34A]">Registrierung</div>
      <h1 className="font-display text-3xl font-bold tracking-tight mt-2">
        {isDealer ? "Neues Händler-Konto" : "Neues Privatkonto"}
      </h1>
      <p className="text-gray-500 mt-2 text-sm">
        {isDealer
          ? "Für Händler prüfen wir das Konto vor der Freischaltung."
          : "Für private Nutzer ist das Konto sofort nutzbar."}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
        <RoleButton
          active={!isDealer}
          icon={User}
          title="Privat"
          text="Für private Käufer und Verkäufer."
          onClick={() => setForm(f => ({ ...f, role: "private", company: "" }))}
          testId="reg-role-private"
        />
        <RoleButton
          active={isDealer}
          icon={Store}
          title="Händler"
          text="Für Autohäuser und gewerbliche Anbieter."
          onClick={() => setForm(f => ({ ...f, role: "dealer" }))}
          testId="reg-role-dealer"
        />
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4">
        {isDealer && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Firma *">
              <input required data-testid="reg-company" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
            </Field>
            <Field label="Steuernummer *">
              <input required data-testid="reg-tax-number" value={form.tax_number} onChange={(e) => setForm({...form, tax_number: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
            </Field>
          </div>
        )}

        <Field label="Anrede *">
          <select required data-testid="reg-salutation" value={form.salutation} onChange={(e) => setForm({...form, salutation: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A] bg-white">
            <option value="">Bitte wählen</option>
            <option value="Herr">Herr</option>
            <option value="Frau">Frau</option>
            <option value="Divers">Divers</option>
          </select>
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Vorname *">
            <input required data-testid="reg-first-name" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
          </Field>
          <Field label="Nachname *">
            <input required data-testid="reg-last-name" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-4">
          <Field label="Straße *">
            <input required data-testid="reg-street" value={form.street} onChange={(e) => setForm({...form, street: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
          </Field>
          <Field label="Nummer *">
            <input required data-testid="reg-house-number" value={form.house_number} onChange={(e) => setForm({...form, house_number: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4">
          <Field label="PLZ *">
            <input required data-testid="reg-zip-code" value={form.zip_code} onChange={(e) => setForm({...form, zip_code: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
          </Field>
          <Field label="Ort *">
            <input required data-testid="reg-city" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
          </Field>
        </div>
        <Field label="Land *">
          <input required data-testid="reg-country" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
        </Field>
        <Field label="E-Mail *">
          <input required type="email" data-testid="reg-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
        </Field>
        <Field label="Telefon">
          <input data-testid="reg-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Passwort * (min. 6 Zeichen)">
            <input required type="password" minLength={6} data-testid="reg-password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
          </Field>
          <Field label="Passwort wiederholen *">
            <input required type="password" minLength={6} data-testid="reg-password-confirm" value={form.password_confirm} onChange={(e) => setForm({...form, password_confirm: e.target.value})} className="w-full mt-2 border border-gray-200 py-3 px-4 outline-none focus:border-[#16A34A]" />
          </Field>
        </div>

        {error && <div data-testid="reg-error" className="text-sm text-red-600 bg-red-50 border border-red-200 p-3">{error}</div>}
        <button data-testid="reg-submit" disabled={loading} className="swiss-btn-primary w-full">{loading ? "Registrieren…" : "Konto erstellen"}</button>
      </form>

      <p className="text-sm text-gray-600 mt-8">
        Bereits registriert? <Link to="/login" data-testid="link-login" className="text-[#16A34A] font-semibold hover:underline">Zum Login</Link>
      </p>
    </div>
  );
}

function RoleButton({ active, icon: Icon, title, text, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left border p-4 transition-all flex gap-3 items-start ${
        active ? "border-[#16A34A] bg-green-50" : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <span className={`w-11 h-11 flex items-center justify-center flex-shrink-0 ${active ? "bg-[#16A34A] text-white" : "bg-gray-100 text-gray-600"}`}>
        <Icon className="w-5 h-5" />
      </span>
      <span>
        <span className="block font-display font-bold text-base text-gray-900">{title}</span>
        <span className="block text-sm text-gray-500 mt-1 leading-snug">{text}</span>
      </span>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="swiss-label">{label}</label>
      {children}
    </div>
  );
}
